import type { Organization } from '@/types/organization'
import type { Address } from '@/model/address.model.ts'
import { trim, validatePostcodeForCountry } from '@/utils/validation'
import { decodeLocation } from '@/utils/html-decoder'

export type LocationCategory = {
  id: number
  createdDate: string
  lastModifiedDate: string
  createdBy: string | null
  extraOptions: unknown | null
  createdByOrganisation: number | null
  lastModifiedBy: string | null
  deleted: boolean
  syncindex: number
  category: string
}

export type LocationResponse = {
  status: number
  message: string
  data: Location
}

export type Location = {
  id: string
  createdDate: string
  lastModifiedDate: string
  createdBy: string
  extraOptions: Record<string, unknown>
  createdByOrganisation: number
  lastModifiedBy: string
  deleted: boolean
  syncindex: number
  naam: string
  locatieUUID: string
  straatnaam: string
  huisNummer: string
  toevoeging?: string | undefined
  postcode: string
  stad: string
  land: string
  vestigingsNummer: string
  locatieType: string
  organisatie: Organization
  locatieCategory: LocationCategory
}

export type LocationType = {
  value: string
}

export type CreateLocalLocation = {
  naam: string
  vestigingsNummer: string
  straatnaam: string
  huisNummer?: number
  toevoeging?: string | undefined
  stad: string
  postcode: string
  land: string
  locatieCategory: LocationCategory | null
  organisatie: { organisatieUUID: string }
  locatieType: LocationType | null
}

export type CreateApiLocation = {
  naam: string
  vestigingsNummer: string
  straatnaam: string
  toevoeging: string
  stad: string
  postcode: string
  land: string
  locatieCategory: LocationCategory | null
  organisatie: { organisatieUUID: string }
  huisNummer: string
  locatieType: string | null
}

export type CreateLocationErrors = Partial<Record<keyof CreateLocalLocation, string>>

export function createLocation(organisatieUUID: string): CreateLocalLocation {
  return {
    naam: '',
    vestigingsNummer: '',
    straatnaam: '',
    stad: '',
    postcode: '',
    land: '',
    locatieCategory: null,
    locatieType: null,
    organisatie: { organisatieUUID },
  }
}

export function convertLocationToAddress(location: Location): Address {
  const decodedLocation = decodeLocation(location)

  return {
    streetName: decodedLocation.straatnaam,
    houseNumber: decodedLocation.huisNummer,
    houseNumberSuffix: decodedLocation.toevoeging,
    postalCode: decodedLocation.postcode,
    city: decodedLocation.stad,
    countryCode: decodedLocation.land,
  }
}

export function isValid(location: Partial<CreateLocalLocation>): location is CreateLocalLocation {
  return Object.keys(validate(location)).length === 0
}

export function validate(location: Partial<CreateLocalLocation>) {
  const errors: CreateLocationErrors = {}

  // Naam (required)
  if (!trim(location.naam)) {
    errors.naam = 'Naam is verplicht'
  }

  // Straatnaam (required)
  if (!trim(location.straatnaam)) {
    errors.straatnaam = 'Straatnaam is verplicht'
  }

  // Huisnummer (optional, but if present must be a positive integer)
  if (location.huisNummer != null && trim(location.huisNummer.toString())) {
    const parsed = Number(trim(location.huisNummer.toString()))
    if (Number.isNaN(parsed)) {
      errors.huisNummer = 'Huisnummer is ongeldig'
    } else if (parsed <= 0) {
      errors.huisNummer = 'Huisnummer moet groter dan 0 zijn'
    }
  }

  // Toevoeging (optional, soft rule)
  if (trim(location.toevoeging) && trim(location.toevoeging).length > 20) {
    errors.toevoeging = 'Toevoeging is te lang'
  }

  // Land (required, but free-form)
  if (!trim(location.land)) {
    errors.land = 'Land is verplicht'
  }

  // Postcode (required, country-dependent validation – assuming same helper)
  if (location.postcode && location.land) {
    const postalCodeError = validatePostcodeForCountry(location.postcode, location.land)
    if (postalCodeError) {
      errors.postcode = postalCodeError
    }
  } else if (!trim(location.postcode)) {
    errors.postcode = 'Postcode is verplicht'
  }

  // Stad (required)
  if (!trim(location.stad)) {
    errors.stad = 'Stad is verplicht'
  }

  if (!location.locatieCategory) {
    errors.locatieCategory = 'Locatie categorie is verplicht'
  }

  if (!location.locatieType) {
    errors.locatieType = 'Locatie type is verplicht'
  }

  return errors
}
